/**
 * React component that is responsible for rendering a "user dropdown" for the navbar
 *
 * Copyright (c) 2020 Propellant Software Corp
 * Author: Kelvin Nguyen
 */
import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem
} from 'reactstrap'
import { getFullName } from 'client/shared/js/utils/stringFormatter'
import Link from 'client/shared/js/components/Link'
import ProfilePhoto from 'client/shared/js/components/ProfilePhoto'
import styles from './NavUserDropdown.module.scss'

const NavUserDropdown = ({ user, navigate, onLogout }) => {
  const LogoutDropdownItem = useMemo(() => (
    <DropdownItem id='logoutDropdownItem' onClick={onLogout}>Log out</DropdownItem>
  ), [onLogout])

  const ExternalDropdownItems = useMemo(() => (
    <>
      {/* TODO: Eventually make this an in-product modal/drawer */}
      <Link to={`https://blog.hipherd.com/tag/whats-new`} className='muted' anchor isNewTab>
        <DropdownItem id='whatsNew'>What's New</DropdownItem>
      </Link>
      <Link to={`https://chrome.google.com/webstore/detail/hipherd-add-to-board/aekgmgaoilbogdmohehiopkekpcfmpfd`} className='muted' anchor isNewTab>
        <DropdownItem id='installChromeApp'>Install Chrome app</DropdownItem>
      </Link>
      <Link to={`https://hipherd.kampsite.co/`} className='muted' anchor isNewTab>
        <DropdownItem id='giveFeedbackDropdownItem'>Give feedback</DropdownItem>
      </Link>
    </>
  ), [])

  return useMemo(() => {
    const { photoUrl, firstName, lastName, username } = user?.profile || {}

    return (
      <UncontrolledDropdown className={cx('user-dropdown', styles['user-dropdown'])} nav>
        <DropdownToggle tag='a' className='nav-link'>
          <ProfilePhoto photoUrl={photoUrl} title={getFullName(firstName, lastName)} size='42px' />
        </DropdownToggle>
        <DropdownMenu className='drop-menu primary' right>
          <DropdownItem id='myProfileDropdownItem' onClick={() => navigate(`/${username}`, false, false)}>My Profile</DropdownItem>
          <DropdownItem id='userSettingsDropdownItem' onClick={() => navigate('/settings', false, false)}>Settings</DropdownItem>
          <DropdownItem divider />
          {ExternalDropdownItems}
          <DropdownItem divider />
          {LogoutDropdownItem}
        </DropdownMenu>
      </UncontrolledDropdown>
    )
  }, [user, navigate, LogoutDropdownItem, ExternalDropdownItems])
}

NavUserDropdown.propTypes = {
  user: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  onLogout: PropTypes.func
}

export default NavUserDropdown
